// The web's spelling checker for Revise ▸ Checks (`design-docs/Revise passes — design.md`, step 6). The Apple apps ask the
// platform's checker (NSSpellChecker, UITextChecker); a browser lends a page none, so the web reads a Hunspell dictionary
// itself — SCOWL's American, Australian and Canadian English (`Web/relay/static/dictionaries`, the licence beside them).
// Only what those dictionaries use is read: prefix and suffix rules with their conditions and cross products, NOSUGGEST,
// ONLYINCOMPOUND (the ordinals' pieces, left out), ICONV, TRY and REP. Every word the dictionary allows is worked out when
// it's read (about 170,000 forms), so checking is a lookup. Case as Hunspell has it: a word in small letters may be
// capitalised; a name keeps its capitals. Guesses as Hunspell makes its first ones: REP's likely misspellings, then one
// edit (two letters swapped, one changed, dropped or added, in TRY's order, or two words run together), then two.
// Not exact to the Mac: a different dictionary.

interface Rule { strip: string; add: string; condition: RegExp }
interface Affix { kind: 'PFX' | 'SFX'; cross: boolean; rules: Rule[] }

export class Speller {
  private readonly words = new Set<string>();
  /** Forms never offered as a guess (NOSUGGEST). */
  private readonly unsuggested = new Set<string>();
  private try = '';
  private rep: [string, string][] = [];
  private letters: string[] | null = null;

  private constructor() {}

  /** Reads a Hunspell affix file and dictionary. */
  static fromHunspell(aff: string, dic: string): Speller {
    const s = new Speller();
    const affixes = new Map<string, Affix>();
    let noSuggest = '', onlyInCompound = '';
    const iconv: [string, string][] = [];
    const lines = aff.split(/\r?\n/);
    for (let i = 0; i < lines.length; i++) {
      const f = lines[i].replace(/#.*$/, '').trim().split(/\s+/);
      if (!f[0]) continue;
      switch (f[0]) {
        case 'TRY': s.try = f[1] ?? ''; break;
        case 'NOSUGGEST': noSuggest = f[1] ?? ''; break;
        case 'ONLYINCOMPOUND': onlyInCompound = f[1] ?? ''; break;
        case 'ICONV': {
          const n = Number(f[1]);
          for (let k = 0; k < n; k++) {
            const g = lines[++i].trim().split(/\s+/);
            iconv.push([g[1], g[2]]);
          }
          break;
        }
        case 'REP': {
          const n = Number(f[1]);
          for (let k = 0; k < n; k++) {
            const g = lines[++i].trim().split(/\s+/);
            s.rep.push([g[1], g[2].replaceAll('_', ' ')]);
          }
          break;
        }
        case 'PFX':
        case 'SFX': {
          if (f.length !== 4) break; // a rule line read by its header
          const kind = f[0], flag = f[1], n = Number(f[3]);
          const affix: Affix = { kind, cross: f[2] === 'Y', rules: [] };
          for (let k = 0; k < n; k++) {
            const g = lines[++i].replace(/#.*$/, '').trim().split(/\s+/);
            const strip = g[2] === '0' ? '' : g[2];
            const add = (g[3] ?? '').split('/')[0];
            const cond = g[4] ?? '.';
            affix.rules.push({ strip, add: add === '0' ? '' : add, condition: new RegExp(cond === '.' ? '' : kind === 'SFX' ? `${cond}$` : `^${cond}`, 'u') });
          }
          affixes.set(flag, affix);
          break;
        }
        default: break;
      }
    }
    const convert = (w: string) => iconv.reduce((x, [a, b]) => x.replaceAll(a, b), w);
    const entries = dic.split(/\r?\n/);
    for (let i = 1; i < entries.length; i++) {
      const line = entries[i].trim();
      if (!line) continue;
      const slash = line.indexOf('/');
      const stem = convert(slash < 0 ? line : line.slice(0, slash));
      const flags = slash < 0 ? '' : line.slice(slash + 1).split(/\s/)[0];
      if (onlyInCompound && flags.includes(onlyInCompound)) continue;
      const forms = Speller.expand(stem, flags, affixes);
      const set = noSuggest && flags.includes(noSuggest) ? s.unsuggested : null;
      for (const form of forms) {
        s.words.add(form);
        set?.add(form);
      }
    }
    return s;
  }

  /** A stem and everything its flags make of it. */
  private static expand(stem: string, flags: string, affixes: Map<string, Affix>): string[] {
    const out = [stem];
    const apply = (word: string, affix: Affix): string[] => {
      const made: string[] = [];
      for (const r of affix.rules) {
        if (!r.condition.test(word)) continue;
        if (affix.kind === 'SFX') {
          if (r.strip && !word.endsWith(r.strip)) continue;
          made.push(word.slice(0, word.length - r.strip.length) + r.add);
        } else {
          if (r.strip && !word.startsWith(r.strip)) continue;
          made.push(r.add + word.slice(r.strip.length));
        }
      }
      return made;
    };
    const suffixed: string[] = [];
    const prefixes: Affix[] = [];
    for (const flag of flags) {
      const affix = affixes.get(flag);
      if (!affix) continue;
      if (affix.kind === 'SFX') {
        const made = apply(stem, affix);
        out.push(...made);
        if (affix.cross) suffixed.push(...made);
      } else prefixes.push(affix);
    }
    for (const p of prefixes) {
      out.push(...apply(stem, p));
      if (p.cross) for (const w of suffixed) out.push(...apply(w, p));
    }
    return out;
  }

  /** How many forms the dictionary allows. */
  get size(): number {
    return this.words.size;
  }

  /** Whether the word is spelled as the dictionary allows (a curly apostrophe reads as a straight one). */
  correct(word: string): boolean {
    const w = word.replaceAll('’', "'");
    if (this.words.has(w)) return true;
    const chars = [...w];
    const rest = chars.slice(1).join('');
    // Capitalised (to start a sentence): the word in small letters.
    if (chars.length && chars[0] !== chars[0].toLowerCase() && rest === rest.toLowerCase()) return this.words.has(w.toLowerCase());
    // In capitals: any form of it.
    if (w === w.toUpperCase() && w !== w.toLowerCase()) {
      const lower = w.toLowerCase();
      return this.words.has(lower) || this.words.has(lower[0].toUpperCase() + lower.slice(1));
    }
    return false;
  }

  /** Up to `limit` guesses for a word, best first, in its case and with its apostrophes. */
  suggest(word: string, limit = 4): string[] {
    const curly = word.includes('’');
    const asked = word.replaceAll('’', "'");
    const chars = [...asked];
    const capitalised = chars.length > 1 && chars[0] !== chars[0].toLowerCase();
    const lower = capitalised ? asked.toLowerCase() : asked;
    const found: string[] = [];
    const seen = new Set<string>([lower]);
    const offer = (candidate: string) => {
      if (seen.has(candidate)) return;
      seen.add(candidate);
      const split = candidate.includes(' ');
      const parts = candidate.split(' ').map((part) => {
        if (!part || this.unsuggested.has(part) || (split && [...part].length === 1 && part !== 'a')) return null;
        if (this.words.has(part)) return part;
        // A name the dictionary keeps capitalised (Wednsday → Wednesday), for a word written with a capital.
        if (!capitalised || split) return null;
        const named = part[0].toUpperCase() + part.slice(1);
        return this.words.has(named) && !this.unsuggested.has(named) ? named : null;
      });
      // Never a lone letter ("Hm" → "M").
      if (parts.every((x) => x !== null) && [...candidate].length > 1) found.push(parts.join(' '));
    };
    for (const [from, to] of this.rep) {
      for (let i = lower.indexOf(from); i >= 0; i = lower.indexOf(from, i + 1)) offer(lower.slice(0, i) + to + lower.slice(i + from.length));
    }
    const first = this.edits(lower);
    for (const e of first) offer(e);
    // Two words run together, each of four letters or more ("foot ways"), or "a" ("alot" is REP's): "bur then" and
    // "hear ted" help nobody.
    const l = [...lower];
    for (let i = 1; i < l.length; i++) {
      const [a, b] = [l.slice(0, i).join(''), l.slice(i).join('')];
      if ((a.length >= 4 || a === 'a') && b.length >= 4) offer(`${a} ${b}`);
    }
    if (found.length < limit && l.length <= 14) {
      // Two edits: looked up without making a set of them (a long word has a few hundred thousand).
      for (const e of first) {
        for (const e2 of this.edits(e)) {
          if (!seen.has(e2) && this.words.has(e2)) offer(e2);
          if (found.length >= limit * 2) break;
        }
        if (found.length >= limit * 2) break;
      }
    }
    const cased = (w: string) => {
      const out = capitalised && w ? w[0].toUpperCase() + w.slice(1) : w;
      return curly ? out.replaceAll("'", '’') : out;
    };
    return found.slice(0, limit).map(cased);
  }

  /** Every word one edit away, in the order Hunspell tries them: two letters swapped, one too many, one forgotten, one
   *  wrong (by TRY's order of letters). */
  private edits(w: string): string[] {
    // By UTF-16 unit: the dictionaries are in the Basic Multilingual Plane.
    const letters = this.letters ??= [...this.try].filter((c) => c === c.toLowerCase());
    const out: string[] = [];
    for (let i = 0; i + 1 < w.length; i++) out.push(w.slice(0, i) + w[i + 1] + w[i] + w.slice(i + 2));
    for (let i = 0; i < w.length; i++) out.push(w.slice(0, i) + w.slice(i + 1));
    for (let i = 0; i <= w.length; i++) for (const c of letters) out.push(w.slice(0, i) + c + w.slice(i));
    for (let i = 0; i < w.length; i++) for (const c of letters) if (c !== w[i]) out.push(w.slice(0, i) + c + w.slice(i + 1));
    return out;
  }
}

/** The dictionaries the web has, by file name. */
export type SpellingDictionary = 'en_US' | 'en_AU' | 'en_CA';

/** The dictionary for a project's language (its `locale`): American, Canadian, or Australian for the other Englishes
 *  (British spelling, with -ise); none for another language, which is checked on the Mac and iPad. */
export function spellingDictionary(locale: string | null | undefined): SpellingDictionary | null {
  const [language, region] = (locale ?? '').split(/[-_]/);
  if (language?.toLowerCase() !== 'en') return null;
  switch (region?.toUpperCase()) {
    case 'US': case undefined: case '': return 'en_US';
    case 'CA': return 'en_CA';
    default: return 'en_AU';
  }
}
